/**
 * SCORM Importer Component
 * Upload and import SCORM packages into courses
 * SUPER_ADMIN only - PHASE 10A (Content Only)
 */
import React, { useState, useEffect } from 'react';
import toast from '../utils/toast';

const API_URL = import.meta.env.VITE_API_URL;

function ScormImporter() {
  const [courses, setCourses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [uploadedPackage, setUploadedPackage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // New state for "Create Course from SCORM" modal
  const [showCreateCourseModal, setShowCreateCourseModal] = useState(false);
  const [createCoursePackageId, setCreateCoursePackageId] = useState(null);
  const [newCourseTitle, setNewCourseTitle] = useState('');
  const [newCourseCertificateType, setNewCourseCertificateType] = useState('general');
  const [newCourseCategoryId, setNewCourseCategoryId] = useState('');

  useEffect(() => {
    fetchCourses();
    fetchPackages();
  }, []);

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCourses(data);
        if (data.length > 0 && !selectedCourseId) {
          setSelectedCourseId(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/scorm/packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    if (!file.name.endsWith('.zip')) {
      alert('Only ZIP files are allowed');
      e.target.value = '';
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setUploadedPackage(null);
    setError(null);
    setSuccess(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a SCORM package (ZIP file)');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_URL}/scorm/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload SCORM package');
      }

      const data = await response.json();
      setUploadedPackage(data);
      setSuccess(`Successfully uploaded SCORM package: ${data.title}`);
      setSelectedFile(null);
      document.getElementById('file-input').value = '';

      // Show success toast
      toast.success(`Successfully uploaded SCORM package: ${data.title}`);

      // Refresh packages list
      await fetchPackages();

    } catch (err) {
      console.error('Error uploading SCORM package:', err);
      setError(err.message || 'Failed to upload SCORM package');

      // Show error toast
      toast.error(err.message || 'Failed to upload SCORM package');
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async (packageId) => {
    if (!selectedCourseId) {
      toast.error('Please select a course');
      return;
    }

    console.log(`[SCORM IMPORT] Starting import - Package ID: ${packageId}, Course ID: ${selectedCourseId}`);

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');

      // NEW ENDPOINT: POST /scorm/import-to-course with JSON body
      const response = await fetch(`${API_URL}/scorm/import-to-course`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          scorm_package_id: packageId,
          course_id: selectedCourseId
        })
      });

      console.log(`[SCORM IMPORT] Response status: ${response.status}`);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[SCORM IMPORT] Error response:', errorData);
        throw new Error(errorData.detail || 'Failed to import SCORM package');
      }

      const data = await response.json();
      console.log('[SCORM IMPORT] Success response:', data);

      // Show success toast
      toast.success(data.message || `Successfully imported ${data.sco_count} SCO(s)`);

      // Also set state-based success message
      setSuccess(data.message);

      // Clear uploaded package preview (if any)
      setUploadedPackage(null);

      // Refresh packages list to show updated import status
      await fetchPackages();

    } catch (err) {
      console.error('[SCORM IMPORT] Error:', err);

      // Show error toast
      toast.error(err.message || 'Failed to import SCORM package');

      // Also set state-based error message
      setError(err.message || 'Failed to import SCORM package');
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (packageId, packageTitle) => {
    // Confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to DELETE the SCORM package "${packageTitle}"?\n\n` +
      `This will:\n` +
      `- Delete all module blocks using this package\n` +
      `- Delete orphaned course modules\n` +
      `- Delete all SCORM runtime records\n` +
      `- Remove filesystem directory\n\n` +
      `THIS CANNOT BE UNDONE!`
    );

    if (!confirmDelete) {
      return;
    }

    console.log(`[SCORM DELETE] Starting delete - Package ID: ${packageId}`);

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/scorm/${packageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log(`[SCORM DELETE] Response status: ${response.status}`);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[SCORM DELETE] Error response:', errorData);
        throw new Error(errorData.detail || 'Failed to delete SCORM package');
      }

      const data = await response.json();
      console.log('[SCORM DELETE] Success response:', data);

      // Show success toast
      toast.success(data.message || 'SCORM package deleted successfully');

      // Also set state-based success message
      setSuccess(data.message);

      // Clear uploaded package preview (if any)
      setUploadedPackage(null);

      // Refresh packages list
      await fetchPackages();

    } catch (err) {
      console.error('[SCORM DELETE] Error:', err);

      // Show error toast
      toast.error(err.message || 'Failed to delete SCORM package');

      // Also set state-based error message
      setError(err.message || 'Failed to delete SCORM package');
    } finally {
      setImporting(false);
    }
  };

  const openCreateCourseModal = (packageId, packageTitle) => {
    setCreateCoursePackageId(packageId);
    setNewCourseTitle(packageTitle); // Pre-fill with package title
    setNewCourseCertificateType('general'); // Default to 'general'
    setNewCourseCategoryId('');
    setShowCreateCourseModal(true);
  };

  const closeCreateCourseModal = () => {
    setShowCreateCourseModal(false);
    setCreateCoursePackageId(null);
    setNewCourseTitle('');
    setNewCourseCertificateType('general');
    setNewCourseCategoryId('');
  };

  const handleCreateCourse = async () => {
    if (!newCourseTitle.trim()) {
      toast.error('Please enter a course title');
      return;
    }

    if (!newCourseCertificateType) {
      toast.error('Please select a certificate type');
      return;
    }

    if (!createCoursePackageId) {
      toast.error('No SCORM package selected');
      return;
    }

    console.log(`[SCORM CREATE COURSE] Starting - Package ID: ${createCoursePackageId}, Title: ${newCourseTitle}, Certificate Type: ${newCourseCertificateType}`);

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');

      const payload = {
        scorm_package_id: createCoursePackageId,
        title: newCourseTitle,
        certificate_type: newCourseCertificateType
      };

      // Add category_id only if provided
      if (newCourseCategoryId && newCourseCategoryId.trim() !== '') {
        payload.category_id = parseInt(newCourseCategoryId);
      }

      const response = await fetch(`${API_URL}/scorm/create-course-from-package`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log(`[SCORM CREATE COURSE] Response status: ${response.status}`);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[SCORM CREATE COURSE] Error response:', errorData);
        throw new Error(errorData.detail || 'Failed to create course from SCORM package');
      }

      const data = await response.json();
      console.log('[SCORM CREATE COURSE] Success response:', data);

      // Show success toast
      toast.success(data.message || `Course created successfully with ${data.sco_count} SCO(s)`);

      // Close modal
      closeCreateCourseModal();

      // Refresh packages list
      await fetchPackages();

      // Redirect to course editor
      console.log(`[SCORM CREATE COURSE] Redirecting to /courses/${data.course_id}`);
      window.location.href = `/courses/${data.course_id}`;

    } catch (err) {
      console.error('[SCORM CREATE COURSE] Error:', err);

      // Show error toast
      toast.error(err.message || 'Failed to create course from SCORM package');

      // Also set state-based error message
      setError(err.message || 'Failed to create course from SCORM package');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
        color: '#1a1a1a',
        padding: '24px 32px',
        borderRadius: '8px',
        marginBottom: '32px',
        boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)'
      }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '2rem', fontWeight: '700' }}>
          SCORM Importer
        </h1>
        <p style={{ margin: 0, fontSize: '1rem', opacity: 0.9 }}>
          Upload and import SCORM packages into your courses
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div style={{
          padding: '16px 24px',
          background: '#ffebee',
          color: '#c62828',
          borderRadius: '8px',
          marginBottom: '24px',
          border: '2px solid #e57373'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '16px 24px',
          background: '#e8f5e9',
          color: '#2e7d32',
          borderRadius: '8px',
          marginBottom: '24px',
          border: '2px solid #4caf50'
        }}>
          {success}
        </div>
      )}

      {/* Upload Section */}
      <div style={{
        background: '#fff',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '32px',
        border: '2px solid #e0e0e0'
      }}>
        <h2 style={{
          margin: '0 0 16px 0',
          fontSize: '1.3rem',
          fontWeight: '700',
          color: '#d4af37',
          borderBottom: '2px solid #d4af37',
          paddingBottom: '8px'
        }}>
          Upload SCORM Package
        </h2>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
            Select SCORM ZIP File
          </label>
          <input
            id="file-input"
            type="file"
            accept=".zip"
            onChange={handleFileChange}
            style={{
              display: 'block',
              width: '100%',
              padding: '12px',
              border: '2px dashed #d4af37',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.95rem'
            }}
          />
        </div>

        {selectedFile && (
          <div style={{
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '16px',
            fontSize: '0.9rem',
            color: '#333'
          }}>
            Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1048576).toFixed(2)} MB)
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          style={{
            padding: '12px 32px',
            background: selectedFile && !uploading ? '#d4af37' : '#ccc',
            color: '#1a1a1a',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedFile && !uploading ? 'pointer' : 'not-allowed',
            fontWeight: '700',
            fontSize: '1rem',
            width: '100%'
          }}
        >
          {uploading ? 'Uploading...' : 'Upload SCORM Package'}
        </button>
      </div>

      {/* Uploaded Package Preview */}
      {uploadedPackage && (
        <div style={{
          background: '#fff',
          padding: '24px',
          borderRadius: '8px',
          marginBottom: '32px',
          border: '2px solid #4caf50'
        }}>
          <h2 style={{
            margin: '0 0 16px 0',
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#4caf50',
            borderBottom: '2px solid #4caf50',
            paddingBottom: '8px'
          }}>
            Uploaded Package Details
          </h2>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>Package Title</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#333' }}>
              {uploadedPackage.title}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>SCO Count</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#333' }}>
              {uploadedPackage.sco_count} launchable item(s)
            </div>
          </div>

          {uploadedPackage.sco_items && uploadedPackage.sco_items.length > 0 && (
            <div>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>SCO Items</div>
              {uploadedPackage.sco_items.map((sco, index) => (
                <div key={index} style={{
                  padding: '12px',
                  background: '#f5f5f5',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ fontWeight: '600', fontSize: '0.95rem', color: '#333', marginBottom: '4px' }}>
                    {sco.title}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    Launch: {sco.launch}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e0e0e0' }}>
            <label style={{ display: 'block', fontWeight: '700', marginBottom: '12px', fontSize: '1.05rem', color: '#333' }}>
              Import to Course
            </label>
            <select
              value={selectedCourseId || ''}
              onChange={(e) => setSelectedCourseId(parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '1rem',
                border: '2px solid #d4af37',
                borderRadius: '4px',
                background: '#fff',
                cursor: 'pointer',
                marginBottom: '16px'
              }}
            >
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>

            <button
              onClick={() => handleImport(uploadedPackage.package_id)}
              disabled={!selectedCourseId || importing}
              style={{
                padding: '12px 32px',
                background: selectedCourseId && !importing ? '#4caf50' : '#ccc',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedCourseId && !importing ? 'pointer' : 'not-allowed',
                fontWeight: '700',
                fontSize: '1rem',
                width: '100%',
                marginBottom: '12px'
              }}
            >
              {importing ? 'Importing...' : 'Import to Course'}
            </button>

            <button
              onClick={() => openCreateCourseModal(uploadedPackage.package_id, uploadedPackage.title)}
              disabled={importing}
              style={{
                padding: '12px 32px',
                background: importing ? '#ccc' : '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: importing ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                fontSize: '1rem',
                width: '100%'
              }}
            >
              {importing ? 'Creating...' : 'Create New Course from This Package'}
            </button>
          </div>
        </div>
      )}

      {/* Existing Packages */}
      {packages.length > 0 && (
        <div style={{
          background: '#fff',
          padding: '24px',
          borderRadius: '8px',
          border: '2px solid #e0e0e0'
        }}>
          <h2 style={{
            margin: '0 0 16px 0',
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#d4af37',
            borderBottom: '2px solid #d4af37',
            paddingBottom: '8px'
          }}>
            Previously Uploaded Packages
          </h2>

          {packages.map((pkg, index) => (
            <div key={pkg.id} style={{
              padding: '16px',
              background: index % 2 === 0 ? '#f9f9f9' : '#fff',
              borderRadius: '4px',
              marginBottom: '12px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700', fontSize: '1rem', color: '#333', marginBottom: '8px' }}>
                    {pkg.title}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                    SCOs: {pkg.sco_count} | Uploaded: {new Date(pkg.uploaded_at).toLocaleString()}
                  </div>
                  {pkg.imported_to_course_id && (
                    <div style={{ fontSize: '0.85rem', color: '#4caf50', fontWeight: '600' }}>
                      Imported to course (ID: {pkg.imported_to_course_id})
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {!pkg.imported_to_course_id && (
                    <>
                      <button
                        onClick={() => handleImport(pkg.id)}
                        disabled={importing || !selectedCourseId}
                        style={{
                          padding: '8px 16px',
                          background: importing ? '#ccc' : '#d4af37',
                          color: '#1a1a1a',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: importing ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        Import
                      </button>
                      <button
                        onClick={() => openCreateCourseModal(pkg.id, pkg.title)}
                        disabled={importing}
                        style={{
                          padding: '8px 16px',
                          background: importing ? '#ccc' : '#2196f3',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: importing ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        Create New Course
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDelete(pkg.id, pkg.title)}
                    disabled={importing}
                    style={{
                      padding: '8px 16px',
                      background: importing ? '#ccc' : '#ff5252',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: importing ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Course Modal */}
      {showCreateCourseModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            padding: '32px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{
              margin: '0 0 16px 0',
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2196f3'
            }}>
              Create New Course from SCORM
            </h2>

            <p style={{
              margin: '0 0 24px 0',
              fontSize: '0.95rem',
              color: '#666'
            }}>
              This will create a new course with modules from the selected SCORM package.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px',
                fontSize: '0.95rem',
                color: '#333'
              }}>
                Course Title <span style={{ color: '#ff5252' }}>*</span>
              </label>
              <input
                type="text"
                value={newCourseTitle}
                onChange={(e) => setNewCourseTitle(e.target.value)}
                placeholder="Enter course title..."
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #d4af37',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px',
                fontSize: '0.95rem',
                color: '#333'
              }}>
                Certificate Type <span style={{ color: '#ff5252' }}>*</span>
              </label>
              <select
                value={newCourseCertificateType}
                onChange={(e) => setNewCourseCertificateType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #d4af37',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  background: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="general">General Certificate</option>
                <option value="iso">ISO Certificate</option>
                <option value="cpd">CPD Certificate</option>
                <option value="scorm">SCORM Certificate</option>
                <option value="none">No Certificate</option>
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                marginBottom: '8px',
                fontSize: '0.95rem',
                color: '#333'
              }}>
                Category ID <span style={{ color: '#999', fontWeight: '400' }}>(Optional)</span>
              </label>
              <input
                type="number"
                value={newCourseCategoryId}
                onChange={(e) => setNewCourseCategoryId(e.target.value)}
                placeholder="Enter category ID (optional)..."
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '1rem',
                  border: '2px solid #d4af37',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeCreateCourseModal}
                disabled={importing}
                style={{
                  padding: '12px 24px',
                  background: '#e0e0e0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: importing ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCourse}
                disabled={!newCourseTitle.trim() || !newCourseCertificateType || importing}
                style={{
                  padding: '12px 24px',
                  background: newCourseTitle.trim() && newCourseCertificateType && !importing ? '#2196f3' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: newCourseTitle.trim() && newCourseCertificateType && !importing ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}
              >
                {importing ? 'Creating...' : 'Create Course'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScormImporter;
